import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiCalendar,
  FiMapPin,
  FiCreditCard,
  FiCheck,
  FiAlertCircle,
  FiPlus,
  FiMinus,
} from "react-icons/fi";
import { mealPlansAPI, usersAPI, subscriptionsAPI } from "../../services/api";
import LoadingSpinner from "../../components/LoadingSpinner";
import PaymentMethodSelector from "../../components/Payment/PaymentMethodSelector";
import mmgPaymentService from "../../services/mmgPayment";
import toast from "react-hot-toast";

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  transition: color 0.2s;

  &:hover {
    color: #2d5016;
  }
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const CheckoutForm = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    height: 2px;
    background: #e0e0e0;
    z-index: 0;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
  background: white;
  padding: 0 1rem;

  .step-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${(props) =>
      props.$active || props.$completed ? "#2d5016" : "#e0e0e0"};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    transition: all 0.3s;
  }

  .step-label {
    font-size: 0.875rem;
    color: ${(props) => (props.$active ? "#2d5016" : "#666")};
    font-weight: ${(props) => (props.$active ? "600" : "400")};
    white-space: nowrap;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const AddressCard = styled.div`
  border: 2px solid ${(props) => (props.$selected ? "#2d5016" : "#e0e0e0")};
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.$selected ? "#f0f8f0" : "white")};

  &:hover {
    border-color: #2d5016;
  }
`;

const AddressText = styled.div`
  color: #666;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const MapLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #4285f4;
  font-size: 0.75rem;
  margin-top: 0.5rem;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &.primary {
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    color: white;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(45, 80, 22, 0.3);
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: white;
    color: #2d5016;
    border: 2px solid #2d5016;

    &:hover {
      background: #2d5016;
      color: white;
    }
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.2rem;
    color: #2d5016;
  }
`;

const MealPlanPreview = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;

  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const OptionCard = styled.div`
  border: 2px solid ${(props) => (props.$selected ? "#2d5016" : "#e0e0e0")};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.$selected ? "#f0f8f0" : "white")};

  &:hover {
    border-color: #2d5016;
  }

  h4 {
    color: #2d5016;
    margin-bottom: 0.5rem;
    font-size: 1.125rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
    margin: 0;
  }
`;

const PickupInfo = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;

  h5 {
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    margin: 0.25rem 0;
    font-size: 0.875rem;
  }
`;

const LocationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;

  &:hover {
    background: #357ae8;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const LocationInfo = styled.div`
  background: #e8f5e9;
  border: 1px solid #4caf50;
  border-radius: 8px;
  padding: 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #2e7d32;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const QuantityLabel = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 1rem;
`;

const QuantityInput = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  background: #f8f9fa;
  border: none;
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2d5016;
  transition: background 0.2s;
  font-size: 1.25rem;

  &:hover {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityValue = styled.div`
  padding: 0.75rem 1.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  min-width: 60px;
  text-align: center;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
`;

const Checkout = () => {
  const { mealPlanId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [fulfillmentMethod, setFulfillmentMethod] = useState("delivery");
  const [paymentMethod, setPaymentMethod] = useState("mmg");
  const [quantity, setQuantity] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    country: "Guyana", // Always Guyana, not shown in UI
    latitude: null,
    longitude: null,
    isDefault: true,
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Fetch meal plan details
  const { data: mealPlanData, isLoading: loadingMealPlan } = useQuery(
    ["mealPlan", mealPlanId],
    () => mealPlansAPI.getMealPlan(mealPlanId),
    {
      enabled: !!mealPlanId,
    }
  );

  // Fetch user addresses
  const {
    data: addressesData,
    isLoading: loadingAddresses,
    refetch: refetchAddresses,
  } = useQuery("addresses", () => usersAPI.getProfile());

  const mealPlan = mealPlanData?.data?.data?.mealPlan;
  const addresses =
    addressesData?.data?.data?.user?.addresses?.filter(
      (addr) => addr.id !== null
    ) || [];

  // Set minimum date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = tomorrow.toISOString().split("T")[0];
    setStartDate(minDate);
  }, []);

  // Auto-select default address
  useEffect(() => {
    if (addresses.length > 0 && !selectedAddress) {
      const defaultAddr = addresses.find((addr) => addr.isDefault);
      setSelectedAddress(defaultAddr || addresses[0]);
    }
  }, [addresses, selectedAddress]);

  const handleAddressInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setNewAddress((prev) => ({
          ...prev,
          latitude,
          longitude,
        }));
        toast.success("Location captured successfully!");
        setGettingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Failed to get location";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location permission denied. Please enable location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out";
            break;
        }

        toast.error(errorMessage);
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleSaveNewAddress = async () => {
    if (!newAddress.addressLine1 || !newAddress.city || !newAddress.region) {
      toast.error("Please fill in all required address fields");
      return;
    }

    setSavingAddress(true);
    try {
      await usersAPI.addAddress(newAddress);
      toast.success("Address added successfully!");

      // Refetch addresses
      const updatedData = await refetchAddresses();
      const newAddresses =
        updatedData.data?.data?.data?.user?.addresses?.filter(
          (addr) => addr.id !== null
        ) || [];

      // Select the newly added address
      const latestAddress = newAddresses[newAddresses.length - 1];
      setSelectedAddress(latestAddress);

      // Reset form and hide it
      setNewAddress({
        addressLine1: "",
        addressLine2: "",
        city: "",
        region: "",
        country: "Guyana", // Always Guyana
        latitude: null,
        longitude: null,
        isDefault: true,
      });
      setShowAddressForm(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add address");
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (fulfillmentMethod === "delivery" && !selectedAddress) {
      toast.error("Please select a delivery address");
      return;
    }

    if (!startDate) {
      toast.error("Please select a start date");
      return;
    }

    setProcessing(true);

    try {
      // Convert date to ISO8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
      // Use UTC to avoid timezone conversion issues
      let startDateISO = null;
      if (startDate) {
        const [year, month, day] = startDate.split("-");
        const date = new Date(
          Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))
        );
        startDateISO = date.toISOString();
      }

      const subscriptionData = {
        meal_plan_id: parseInt(mealPlanId),
        start_date: startDateISO,
        fulfillment_method: fulfillmentMethod,
        quantity: quantity,
      };

      // Only include special_instructions if it's not empty
      if (specialInstructions && specialInstructions.trim().length > 0) {
        subscriptionData.special_instructions = specialInstructions.trim();
      }

      // Only include delivery address if delivery method is selected
      if (fulfillmentMethod === "delivery") {
        subscriptionData.delivery_address_id = parseInt(selectedAddress.id);
      }

      console.log("Subscription data being sent:", subscriptionData);

      if (paymentMethod === "mmg") {
        // For MMG payments, create subscriptions first, then redirect to payment
        const response =
          await subscriptionsAPI.createSubscription(subscriptionData);
        const subscription = response.data.data.subscription;

        // DEVELOPMENT MODE: Skip MMG payment and show success
        // TODO: Remove this when MMG API credentials are available
        console.log(
          "DEV MODE: MMG payment skipped - subscription created successfully"
        );

        toast.success(
          `Successfully created ${quantity} subscription${quantity > 1 ? "s" : ""}! Payment processed (Development Mode).`
        );
        navigate("/subscriptions");

        // PRODUCTION CODE (commented out for development):
        // Generate MMG checkout URL
        // const amount = parseFloat(mealPlan.price) * 100 * quantity; // Convert to cents
        // const description = `NourishGY Subscription - ${mealPlan.title} (x${quantity})`;
        // const { url: checkoutUrl, transactionId } =
        //   mmgPaymentService.generateCheckoutUrl(
        //     amount,
        //     description,
        //     subscription.id
        // );
        // localStorage.setItem("mmg_transaction_id", transactionId);
        // localStorage.setItem("subscription_id", subscription.id);
        // window.location.href = checkoutUrl;
      } else {
        // For cash payments, create subscriptions directly
        const response =
          await subscriptionsAPI.createSubscription(subscriptionData);

        toast.success(
          `Successfully created ${quantity} subscription${quantity > 1 ? "s" : ""}! You will pay with cash upon delivery/pickup.`
        );
        navigate("/subscriptions");
      }
    } catch (error) {
      console.error("Subscription creation error:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to create subscription";
      const validationErrors = error.response?.data?.errors;

      if (validationErrors && validationErrors.length > 0) {
        const errorDetails = validationErrors
          .map((err) => `${err.param}: ${err.msg}`)
          .join(", ");
        toast.error(`Validation failed: ${errorDetails}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loadingMealPlan || loadingAddresses) {
    return (
      <Container>
        <Content>
          <LoadingSpinner />
        </Content>
      </Container>
    );
  }

  if (!mealPlan) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <FiAlertCircle />
            Meal plan not found
          </ErrorMessage>
          <Button className="secondary" onClick={() => navigate("/meal-plans")}>
            <FiArrowLeft />
            Back to Meal Plans
          </Button>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <BackButton onClick={() => navigate(-1)}>
          <FiArrowLeft />
          Back
        </BackButton>

        <CheckoutGrid>
          <CheckoutForm>
            <SectionTitle>Purchase Meal Plan</SectionTitle>

            <StepIndicator>
              <Step $active={currentStep === 1} $completed={currentStep > 1}>
                <div className="step-circle">
                  {currentStep > 1 ? <FiCheck /> : "1"}
                </div>
                <div className="step-label">Delivery</div>
              </Step>
              <Step $active={currentStep === 2} $completed={currentStep > 2}>
                <div className="step-circle">
                  {currentStep > 2 ? <FiCheck /> : "2"}
                </div>
                <div className="step-label">Payment & Schedule</div>
              </Step>
              <Step $active={currentStep === 3}>
                <div className="step-circle">3</div>
                <div className="step-label">Review</div>
              </Step>
            </StepIndicator>

            <form onSubmit={handleSubmit}>
              {currentStep === 1 && (
                <FormSection>
                  <Label>Choose Fulfillment Method</Label>
                  {(mealPlan.offersDelivery || mealPlan.offers_delivery) && (
                    <OptionCard
                      $selected={fulfillmentMethod === "delivery"}
                      onClick={() => setFulfillmentMethod("delivery")}
                    >
                      <h4>🚚 Delivery</h4>
                      <p>
                        Get your meals delivered to your doorstep within{" "}
                        {(mealPlan.deliveryRadius || mealPlan.delivery_radius)}km radius
                      </p>
                    </OptionCard>
                  )}
                  {(mealPlan.offersPickup || mealPlan.offers_pickup) && (
                    <OptionCard
                      $selected={fulfillmentMethod === "pickup"}
                      onClick={() => setFulfillmentMethod("pickup")}
                    >
                      <h4>📍 Pickup</h4>
                      <p>Pick up your meals at the vendor location</p>
                      {fulfillmentMethod === "pickup" && (
                        <PickupInfo>
                          <h5>Pickup Location:</h5>
                          {(mealPlan.businessAddress || mealPlan.business_address) && (
                            <p>{mealPlan.businessAddress || mealPlan.business_address}</p>
                          )}
                          {!(mealPlan.businessAddress || mealPlan.business_address) &&
                            (mealPlan.pickupAddress || mealPlan.pickup_address) && (
                              <p>{mealPlan.pickupAddress || mealPlan.pickup_address}</p>
                            )}
                          {(mealPlan.pickupLatitude || mealPlan.pickup_latitude) &&
                            (mealPlan.pickupLongitude || mealPlan.pickup_longitude) && (
                              <>
                                <p
                                  style={{
                                    marginTop: "0.5rem",
                                    fontSize: "0.8rem",
                                    color: "#666",
                                  }}
                                >
                                  📍 Coordinates:{" "}
                                  {parseFloat(mealPlan.pickupLatitude || mealPlan.pickup_latitude).toFixed(
                                    6
                                  )}
                                  ,{" "}
                                  {parseFloat(
                                    mealPlan.pickupLongitude || mealPlan.pickup_longitude
                                  ).toFixed(6)}
                                </p>
                                <MapLink
                                  href={`https://www.google.com/maps?q=${mealPlan.pickupLatitude || mealPlan.pickup_latitude},${mealPlan.pickupLongitude || mealPlan.pickup_longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FiMapPin /> View on Google Maps
                                </MapLink>
                              </>
                            )}
                          {(mealPlan.pickupInstructions || mealPlan.pickup_instructions) && (
                            <>
                              <h5 style={{ marginTop: "0.5rem" }}>
                                Instructions:
                              </h5>
                              <p>{mealPlan.pickupInstructions || mealPlan.pickup_instructions}</p>
                            </>
                          )}
                        </PickupInfo>
                      )}
                    </OptionCard>
                  )}

                  <QuantityControl style={{ marginTop: "1.5rem" }}>
                    <QuantityLabel>Quantity:</QuantityLabel>
                    <QuantityInput>
                      <QuantityButton
                        type="button"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                      >
                        <FiMinus />
                      </QuantityButton>
                      <QuantityValue>{quantity}</QuantityValue>
                      <QuantityButton
                        type="button"
                        onClick={() => setQuantity(quantity + 1)}
                      >
                        <FiPlus />
                      </QuantityButton>
                    </QuantityInput>
                  </QuantityControl>

                  {fulfillmentMethod === "delivery" && (
                    <>
                      <Label style={{ marginTop: "1.5rem" }}>
                        <FiMapPin /> Select Delivery Address
                      </Label>
                      {addresses.length === 0 && !showAddressForm ? (
                        <div>
                          <ErrorMessage>
                            <FiAlertCircle />
                            No addresses found. Please add a delivery address.
                          </ErrorMessage>
                          <Button
                            type="button"
                            className="secondary"
                            onClick={() => setShowAddressForm(true)}
                            style={{ width: "100%", marginTop: "1rem" }}
                          >
                            Add Delivery Address
                          </Button>
                        </div>
                      ) : addresses.length === 0 && showAddressForm ? (
                        <div
                          style={{
                            border: "2px solid #2d5016",
                            borderRadius: "12px",
                            padding: "1.5rem",
                            background: "#f0f8f0",
                            marginBottom: "1rem",
                          }}
                        >
                          <h4
                            style={{ marginBottom: "1rem", color: "#2d5016" }}
                          >
                            New Delivery Address
                          </h4>
                          <FormSection>
                            <Label>📍 GPS Location (Recommended)</Label>
                            <LocationButton
                              type="button"
                              onClick={handleGetLocation}
                              disabled={gettingLocation}
                            >
                              <FiMapPin />
                              {gettingLocation
                                ? "Getting Location..."
                                : newAddress.latitude
                                  ? "📍 Location Captured - Click to Update"
                                  : "📍 Use My Current Location"}
                            </LocationButton>
                            {newAddress.latitude && newAddress.longitude && (
                              <LocationInfo>
                                <FiCheck />
                                Location: {newAddress.latitude.toFixed(6)},{" "}
                                {newAddress.longitude.toFixed(6)}
                              </LocationInfo>
                            )}
                          </FormSection>
                          <FormSection>
                            <Label>Street Address *</Label>
                            <Input
                              name="addressLine1"
                              value={newAddress.addressLine1}
                              onChange={handleAddressInputChange}
                              placeholder="Street address"
                              required
                            />
                          </FormSection>
                          <FormSection>
                            <Label>Address Line 2</Label>
                            <Input
                              name="addressLine2"
                              value={newAddress.addressLine2}
                              onChange={handleAddressInputChange}
                              placeholder="Apartment, suite, etc. (optional)"
                            />
                          </FormSection>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "1rem",
                            }}
                          >
                            <FormSection>
                              <Label>City *</Label>
                              <Input
                                name="city"
                                value={newAddress.city}
                                onChange={handleAddressInputChange}
                                required
                              />
                            </FormSection>
                            <FormSection>
                              <Label>Region/State *</Label>
                              <Input
                                name="region"
                                value={newAddress.region}
                                onChange={handleAddressInputChange}
                                required
                              />
                            </FormSection>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "1rem",
                              marginTop: "1rem",
                            }}
                          >
                            <Button
                              type="button"
                              className="secondary"
                              onClick={() => setShowAddressForm(false)}
                              style={{ flex: 1 }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              className="primary"
                              disabled={savingAddress}
                              style={{ flex: 1 }}
                              onClick={handleSaveNewAddress}
                            >
                              {savingAddress ? "Saving..." : "Save Address"}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {addresses.map((address) => (
                            <AddressCard
                              key={address.id}
                              $selected={selectedAddress?.id === address.id}
                              onClick={() => setSelectedAddress(address)}
                            >
                              <AddressText>
                                <strong>
                                  {address.addressLine1}
                                  {address.isDefault && " (Default)"}
                                </strong>
                                <br />
                                {address.addressLine2 && (
                                  <>
                                    {address.addressLine2}
                                    <br />
                                  </>
                                )}
                                {address.city}, {address.region}, Guyana
                                {address.latitude && address.longitude && (
                                  <>
                                    <br />
                                    <MapLink
                                      href={`https://www.google.com/maps?q=${address.latitude},${address.longitude}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <FiMapPin /> View on Google Maps
                                    </MapLink>
                                  </>
                                )}
                              </AddressText>
                            </AddressCard>
                          ))}
                          {!showAddressForm && (
                            <Button
                              type="button"
                              className="secondary"
                              onClick={() => setShowAddressForm(true)}
                              style={{ width: "100%", marginTop: "1rem" }}
                            >
                              Add Another Address
                            </Button>
                          )}
                          {showAddressForm && (
                            <div style={{ marginTop: "1rem" }}>
                              <div
                                style={{
                                  border: "2px solid #2d5016",
                                  borderRadius: "12px",
                                  padding: "1.5rem",
                                  background: "#f0f8f0",
                                }}
                              >
                                <h4
                                  style={{
                                    marginBottom: "1rem",
                                    color: "#2d5016",
                                  }}
                                >
                                  New Delivery Address
                                </h4>
                                <FormSection>
                                  <Label>📍 GPS Location (Recommended)</Label>
                                  <LocationButton
                                    type="button"
                                    onClick={handleGetLocation}
                                    disabled={gettingLocation}
                                  >
                                    <FiMapPin />
                                    {gettingLocation
                                      ? "Getting Location..."
                                      : newAddress.latitude
                                        ? "📍 Location Captured - Click to Update"
                                        : "📍 Use My Current Location"}
                                  </LocationButton>
                                  {newAddress.latitude &&
                                    newAddress.longitude && (
                                      <LocationInfo>
                                        <FiCheck />
                                        Location:{" "}
                                        {newAddress.latitude.toFixed(6)},{" "}
                                        {newAddress.longitude.toFixed(6)}
                                      </LocationInfo>
                                    )}
                                </FormSection>
                                <FormSection>
                                  <Label>Street Address *</Label>
                                  <Input
                                    name="addressLine1"
                                    value={newAddress.addressLine1}
                                    onChange={handleAddressInputChange}
                                    placeholder="Street address"
                                    required
                                  />
                                </FormSection>
                                <FormSection>
                                  <Label>Address Line 2</Label>
                                  <Input
                                    name="addressLine2"
                                    value={newAddress.addressLine2}
                                    onChange={handleAddressInputChange}
                                    placeholder="Apartment, suite, etc. (optional)"
                                  />
                                </FormSection>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "1rem",
                                  }}
                                >
                                  <FormSection>
                                    <Label>City *</Label>
                                    <Input
                                      name="city"
                                      value={newAddress.city}
                                      onChange={handleAddressInputChange}
                                      required
                                    />
                                  </FormSection>
                                  <FormSection>
                                    <Label>Region/State *</Label>
                                    <Input
                                      name="region"
                                      value={newAddress.region}
                                      onChange={handleAddressInputChange}
                                      required
                                    />
                                  </FormSection>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "1rem",
                                    marginTop: "1rem",
                                  }}
                                >
                                  <Button
                                    type="button"
                                    className="secondary"
                                    onClick={() => {
                                      setShowAddressForm(false);
                                      setNewAddress({
                                        addressLine1: "",
                                        addressLine2: "",
                                        city: "",
                                        region: "",
                                        country: "Guyana", // Always Guyana
                                        latitude: null,
                                        longitude: null,
                                        isDefault: false,
                                      });
                                    }}
                                    style={{ flex: 1 }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    className="primary"
                                    disabled={savingAddress}
                                    style={{ flex: 1 }}
                                    onClick={handleSaveNewAddress}
                                  >
                                    {savingAddress
                                      ? "Saving..."
                                      : "Save Address"}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                  <Button
                    type="button"
                    className="primary"
                    onClick={() => setCurrentStep(2)}
                    disabled={
                      fulfillmentMethod === "delivery" && !selectedAddress
                    }
                  >
                    Continue
                  </Button>
                </FormSection>
              )}

              {currentStep === 2 && (
                <FormSection>
                  <PaymentMethodSelector
                    selectedMethod={paymentMethod}
                    onMethodChange={setPaymentMethod}
                    fulfillmentMethod={fulfillmentMethod}
                    mealPlan={mealPlan}
                  />

                  <Label>
                    <FiCalendar /> Start Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={(() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      return tomorrow.toISOString().split("T")[0];
                    })()}
                    required
                  />
                  <Label style={{ marginTop: "1.5rem" }}>
                    Special Instructions (Optional)
                  </Label>
                  <TextArea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Any dietary restrictions, delivery instructions, etc."
                    maxLength={500}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                      marginTop: "1rem",
                    }}
                  >
                    <Button
                      type="button"
                      className="secondary"
                      onClick={() => setCurrentStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      className="primary"
                      onClick={() => setCurrentStep(3)}
                    >
                      Continue
                    </Button>
                  </div>
                </FormSection>
              )}

              {currentStep === 3 && (
                <FormSection>
                  <Label>Review Your Subscription</Label>
                  <div
                    style={{
                      background: "#f8f9fa",
                      padding: "1rem",
                      borderRadius: "8px",
                      marginBottom: "1rem",
                    }}
                  >
                    <h4>Fulfillment Method:</h4>
                    <p>
                      {fulfillmentMethod === "delivery"
                        ? "🚚 Delivery"
                        : "📍 Pickup"}
                    </p>
                    {fulfillmentMethod === "delivery" ? (
                      <>
                        <h4 style={{ marginTop: "1rem" }}>Delivery Address:</h4>
                        <p>
                          {selectedAddress?.addressLine1}
                          {selectedAddress?.addressLine2 &&
                            `, ${selectedAddress.addressLine2}`}
                          <br />
                          {selectedAddress?.city}, {selectedAddress?.region},
                          Guyana
                        </p>
                      </>
                    ) : (
                      <>
                        <h4 style={{ marginTop: "1rem" }}>Pickup Location:</h4>
                        {(mealPlan.businessAddress || mealPlan.business_address) && (
                          <p>{mealPlan.businessAddress || mealPlan.business_address}</p>
                        )}
                        {!(mealPlan.businessAddress || mealPlan.business_address) &&
                          (mealPlan.pickupAddress || mealPlan.pickup_address) && (
                            <p>{mealPlan.pickupAddress || mealPlan.pickup_address}</p>
                          )}
                        {(mealPlan.pickupLatitude || mealPlan.pickup_latitude) &&
                          (mealPlan.pickupLongitude || mealPlan.pickup_longitude) && (
                            <>
                              <p
                                style={{
                                  marginTop: "0.5rem",
                                  fontSize: "0.875rem",
                                  color: "#666",
                                }}
                              >
                                📍 Coordinates:{" "}
                                {parseFloat(mealPlan.pickupLatitude || mealPlan.pickup_latitude).toFixed(
                                  6
                                )}
                                ,{" "}
                                {parseFloat(mealPlan.pickupLongitude || mealPlan.pickup_longitude).toFixed(
                                  6
                                )}
                              </p>
                              <MapLink
                                href={`https://www.google.com/maps?q=${mealPlan.pickupLatitude || mealPlan.pickup_latitude},${mealPlan.pickupLongitude || mealPlan.pickup_longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FiMapPin /> View on Google Maps
                              </MapLink>
                            </>
                          )}
                        {(mealPlan.pickupInstructions || mealPlan.pickup_instructions) && (
                          <>
                            <h4 style={{ marginTop: "1rem" }}>
                              Pickup Instructions:
                            </h4>
                            <p>{mealPlan.pickupInstructions || mealPlan.pickup_instructions}</p>
                          </>
                        )}
                      </>
                    )}
                    <h4 style={{ marginTop: "1rem" }}>Quantity:</h4>
                    <p>{quantity} subscription{quantity > 1 ? "s" : ""}</p>
                    <h4 style={{ marginTop: "1rem" }}>Start Date:</h4>
                    <p>
                      {startDate
                        ? (() => {
                            const [year, month, day] = startDate.split("-");
                            const date = new Date(
                              parseInt(year),
                              parseInt(month) - 1,
                              parseInt(day)
                            );
                            return date.toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            });
                          })()
                        : ""}
                    </p>
                    {specialInstructions && (
                      <>
                        <h4 style={{ marginTop: "1rem" }}>
                          Special Instructions:
                        </h4>
                        <p>{specialInstructions}</p>
                      </>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                    }}
                  >
                    <Button
                      type="button"
                      className="secondary"
                      onClick={() => setCurrentStep(2)}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="primary"
                      disabled={processing}
                    >
                      <FiCreditCard />
                      {processing ? "Processing..." : "Complete Purchase"}
                    </Button>
                  </div>
                </FormSection>
              )}
            </form>
          </CheckoutForm>

          <OrderSummary>
            <SectionTitle>Order Summary</SectionTitle>
            <MealPlanPreview>
              {mealPlan.image_url && (
                <img src={mealPlan.image_url} alt={mealPlan.title} />
              )}
              <h3>{mealPlan.title}</h3>
              <p>{mealPlan.business_name}</p>
            </MealPlanPreview>

            <SummaryItem>
              <span>Plan Type</span>
              <span>{mealPlan.plan_type}</span>
            </SummaryItem>
            <SummaryItem>
              <span>Duration</span>
              <span>{mealPlan.duration_days} days</span>
            </SummaryItem>
            <SummaryItem>
              <span>Price per plan</span>
              <span>
                GYD ${(() => {
                  const basePrice = parseFloat(mealPlan.price || mealPlan.base_price || 0);
                  const deliveryFee = fulfillmentMethod === "delivery" ? parseFloat(mealPlan.delivery_fee || 0) : 0;
                  return (basePrice + deliveryFee).toLocaleString();
                })()}
              </span>
            </SummaryItem>
            <SummaryItem>
              <span>Quantity</span>
              <span>{quantity}</span>
            </SummaryItem>
            <SummaryItem>
              <span>Total</span>
              <span>
                GYD ${(() => {
                  const basePrice = parseFloat(mealPlan.price || mealPlan.base_price || 0);
                  const deliveryFee = fulfillmentMethod === "delivery" ? parseFloat(mealPlan.delivery_fee || 0) : 0;
                  return ((basePrice + deliveryFee) * quantity).toLocaleString();
                })()}
              </span>
            </SummaryItem>
          </OrderSummary>
        </CheckoutGrid>
      </Content>
    </Container>
  );
};

export default Checkout;
